import {
  AHSeparator,
  AHText,
  Box,
  Circle,
  Dialog,
  LegacyFilter,
  TouchableBox,
  useLayoutContext,
} from "arkh";
import React, { useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import * as Haptics from "expo-haptics";
import { Platform, ScrollView } from "react-native";
import { OrgButton } from "@/src/features/OrgHome/OrgButton";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useFocusEffect } from "@react-navigation/native";

export const ALL_WEEK_NUMBERS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22,
  23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41,
  42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53,
];
const evenWeeks = ALL_WEEK_NUMBERS.filter((w) => w % 2 === 0);
const oddWeeks = ALL_WEEK_NUMBERS.filter((w) => w % 2 !== 0);
export function isAllWeekNumbers(weekNumbers: number[]): boolean {
  return (
    weekNumbers.length === ALL_WEEK_NUMBERS.length &&
    weekNumbers.every((w) => ALL_WEEK_NUMBERS.includes(w))
  );
}
export function isAllEvenWeeks(weekNumbers: number[]): boolean {
  return (
    evenWeeks.length === weekNumbers.length &&
    weekNumbers.every((w) => evenWeeks.includes(w))
  );
}
export function isAllOddWeeks(weekNumbers: number[]): boolean {
  return (
    oddWeeks.length === weekNumbers.length &&
    weekNumbers.every((w) => oddWeeks.includes(w))
  );
}

function WeekNumberPicker(props: {
  onPressWeekNumber(weekNumber: number): void;
  selectedWeekNumbers: number[];
}) {
  return (
    <Box
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
      }}
      space="small"
    >
      {ALL_WEEK_NUMBERS.map((weekNumber) => {
        const isSelected = props.selectedWeekNumbers.includes(weekNumber);
        return (
          <TouchableBox
            key={weekNumber}
            space="small"
            style={{ alignItems: "center" }}
            onPressIn={() => {
              if (Platform.OS === "ios") {
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
              }
            }}
            onPress={() => {
              props.onPressWeekNumber(weekNumber);
            }}
          >
            <Circle
              size="medium"
              color={isSelected ? "status-active" : "surface-backgroundControl"}
            >
              <AHText
                type="info-small"
                color={isSelected ? "text-inverted" : "text-primary"}
              >
                {weekNumber}
              </AHText>
            </Circle>
          </TouchableBox>
        );
      })}
    </Box>
  );
}

export function WeekNumberPickerDialog(props: {
  setWeeks(weeks: number[]): void;
  weeks: number[];
  visible: boolean;
  onClose(): void;
}) {
  const intl = useIntl();
  const intl_tokens = useIntlTokens();
  const layout = useLayoutContext();
  const [selectedValue, setSelectedValue] = useState<number[]>(props.weeks);
  useEffect(() => {
    setSelectedValue(props.weeks);
  }, [props.weeks]);
  useFocusEffect(
    useCallback(() => {
      setSelectedValue(props.weeks);
    }, [props.weeks])
  );
  return (
    <Dialog
      maxHeight={"80%"}
      height={570}
      visible={props.visible}
      onClose={props.onClose}
      title={intl.formatMessage({
        defaultMessage: "Select active weeks",
        description:
          "RoutineEditScheduleWeeklyOptions select active weeks title",
      })}
    >
      <LegacyFilter
        filters={[
          {
            label: intl_tokens.All,
            selected: isAllWeekNumbers(selectedValue),
            id: "all",
          },
          {
            label: intl_tokens.Odd,
            selected: isAllOddWeeks(selectedValue),
            id: "odd",
          },
          {
            label: intl_tokens.Even,
            selected: isAllEvenWeeks(selectedValue),
            id: "even",
          },
        ]}
        onPressFilter={(filter) => {
          if (filter.id === "all") {
            setSelectedValue(ALL_WEEK_NUMBERS);
          } else if (filter.id === "odd") {
            setSelectedValue(oddWeeks);
          } else if (filter.id === "even") {
            setSelectedValue(evenWeeks);
          }
        }}
        ListFooterComponent={
          <TouchableBox
            onPress={() => {
              setSelectedValue([]);
            }}
            style={{
              flex: 1,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
              height: layout.circle.medium,
            }}
            spaceHorizontal="medium"
            hitSlop={{ top: 10, bottom: 10 }}
          >
            <AHText type="subtitle" color="text-secondary">
              <FormattedMessage
                defaultMessage="Clear"
                description="RoutineEditScheduleWeeklyOptions clear filter label"
              />
            </AHText>
          </TouchableBox>
        }
      />
      <ScrollView style={{ flex: 1 }} stickyHeaderIndices={[0]}>
        <AHSeparator spaceHorizontal="medium" />
        <WeekNumberPicker
          onPressWeekNumber={(weekNumber) => {
            if (selectedValue.includes(weekNumber)) {
              setSelectedValue(selectedValue.filter((w) => w !== weekNumber));
            } else {
              setSelectedValue([...selectedValue, weekNumber]);
            }
          }}
          selectedWeekNumbers={selectedValue}
        />
      </ScrollView>
      <AHSeparator spaceHorizontal="medium" />
      <Box space="medium">
        {selectedValue.length > 0 ? (
          <OrgButton
            title={intl_tokens.Done}
            backgroundColor="primary"
            tintColor="text-inverted"
            onPress={() => {
              props.setWeeks(selectedValue);
              props.onClose();
            }}
          />
        ) : (
          <OrgButton
            title={
              <FormattedMessage
                defaultMessage="Select at least 1 active week"
                description="RoutineEditScheduleWeeklyOptions select at least one active week"
              />
            }
            backgroundColor="transparent"
            tintColor="text-primary"
            disabled
          />
        )}
      </Box>
    </Dialog>
  );
}
